import React, { useCallback, useEffect } from 'react';
import { Box, JoyKitThemeProvider, TextV2, ButtonV2, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { EventRSVPAttendanceStatus, useGetEventCTAButtonsDataLazyQuery, useLogoutEventSessionMutation } from '@graphql/generated';
import { useTranslation } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGuestGuideTelemetry } from './GuestGuideTelemetry';
import { AddToCalendarAction } from './GuestGuideActions/AddToCalendarAction';
import { CarRentalsAction } from './GuestGuideActions/CarRentalsAction';
import { HotelDealsAction } from './GuestGuideActions/HotelDealsAction';
import { BrowseCouplesRegistryAction } from './GuestGuideActions/BrowseCouplesRegistryAction';
import { RemindMeToBookStayAction } from './GuestGuideActions/RemindMeToBookStayAction';
import { RsvpStatusSection } from './RsvpStatusSection';
import { CountDownBannerExperiment } from './CountDownBannerExperiment';

type GuestGuideContentProps = Readonly<{
  countDownDate?: number;
  sessionData: ReturnType<typeof useGetEventCTAButtonsDataLazyQuery>[1]['data'];
}>;

export const GuestGuideContent = ({ countDownDate, sessionData }: GuestGuideContentProps) => {
  const { tq } = useTranslation('guestGuide');
  const telemetry = useGuestGuideTelemetry();
  const [logoutEvent, { loading: logoutLoading }] = useLogoutEventSessionMutation();
  const { eventInfo } = useEventInfo();
  const rsvpAttendanceStatus = sessionData?.eventByName?.activeSession?.eventCTA?.rsvpAttendanceStatus;
  const isAttending = rsvpAttendanceStatus === EventRSVPAttendanceStatus.attending;

  const onLogoutHandle = useCallback(() => {
    if (!eventInfo?.eventId) {
      return;
    }

    logoutEvent({
      variables: { eventId: eventInfo.eventId },
      refetchQueries: () => ['GetEventSession', 'GetGuestSiteSchedule', 'GetEventCTAButtonsData'],
    })
      .then(() => {
        telemetry.clickLogout();
      })
      .catch(err => {
        telemetry.trackError('GuestGuideLogout', err);
      });
  }, [logoutEvent, eventInfo?.eventId, telemetry]);

  useEffect(() => {
    telemetry.guideShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <JoyKitThemeProvider>
      <Box
        backgroundColor={'white'}
        color={'inherit'}
        padding={6}
        minWidth={{ _: undefined, sm2: pxToRem(404) }}
      >
        {countDownDate ? <CountDownBannerExperiment dateMs={countDownDate} /> : null}
        <RsvpStatusSection sessionData={sessionData} />
        <Box
          typographyVariant={'hed4'}
          textAlign={['center', 'left']}
        >
          {tq`Guest guide to what's next.`}
        </Box>
        <Box
          marginY={6}
          display={'grid'}
          rowGap={pxToRem(12)}
        >
          {isAttending && <AddToCalendarAction />}
          <HotelDealsAction isAttending={isAttending} />
          <CarRentalsAction />
          {isAttending && <RemindMeToBookStayAction />}
          <BrowseCouplesRegistryAction />
          <Flex
            alignItems="center"
            justifyContent="center"
            gap={3}
            marginTop={6}
          >
            <TextV2
              typographyVariant="label3"
              color="mono8"
            >
              {tq`Not You?`}
            </TextV2>

            <ButtonV2
              typographyVariant="label3"
              intent="neutral"
              color="mono8"
              variant="ghost"
              width="fit-content"
              height="fit-content"
              padding={0}
              loading={logoutLoading}
              onClick={onLogoutHandle}
              borderBottom={logoutLoading ? 'none' : '1px solid'}
              borderBottomColor={logoutLoading ? 'transparent' : 'mono5'}
              borderBottomLeftRadius="0"
              borderBottomRightRadius="0"
            >
              {tq`Sign Out`}
            </ButtonV2>
          </Flex>
        </Box>
      </Box>
    </JoyKitThemeProvider>
  );
};
